<template>
    <div
        class="veui-steps" ui="m medium"
        role="list"
    >
        <template v-for="(step, index) in steps">
            <div
                v-if="index !== 0"
                :key="`connector-${index}`"
                :class="[
                    'veui-steps-step-connector',
                    `veui-steps-step-connector-${stepStatus[index]}`,
                ]"
            />
            <veui-link
                :key="`step-${index}`"
                :class="[
                    'veui-steps-step',
                    `veui-steps-step-${stepStatus[index]}`,
                    index < steps.length - 1
                        ? `veui-steps-step-next-${stepStatus[index + 1]}`
                        : '',
                    {['veui-steps-step-activatable']: !stateless && !!step.to},
                ]"
                :to="stateless ? null : step.to"
                fallback="div"
                role="listitem"
                :aria-current="index === current ? 'step' : null"
                @click="$emit('click', index, $event)"
            >
                <slot v-bind="step" :index="index">
                    <div class="veui-steps-step-index-container">
                        <div class="veui-steps-step-index">
                            <slot
                                name="index" v-bind="step"
                                :index="index"
                            >
                                <veui-icon
                                    v-if="
                                        !stateless && index < current && step.status !== 'error'
                                    "
                                    name="check"
                                />
                                <veui-icon
                                    v-else-if="
                                        !stateless && index > current && step.status === 'success'
                                    "
                                    name="check"
                                />
                                <veui-icon
                                    v-else-if="!stateless && step.status === 'error'"
                                    name="times"
                                />
                                <template v-else>{{ index + 1 }}</template>
                            </slot>
                        </div>
                        <div
                            v-if="index < stepStatus.length - 1"
                            :class="[
                                'veui-steps-step-connector-placeholder',
                                `veui-steps-step-connector-placeholder-${
                                    stepStatus[index + 1]
                                }`,
                            ]"
                        />
                    </div>
                    <div v-if="step.label" class="veui-steps-step-content">
                        <div class="veui-steps-step-label">
                            <slot
                                name="label"
                                v-bind="step"
                                :index="index"
                            >
                                {{ step.label }}
                            </slot>
                        </div>
                        <div v-if="step.desc" class="veui-steps-step-desc">
                            <slot
                                name="desc"
                                v-bind="step"
                                :index="index"
                            >
                                {{ step.desc }}
                            </slot>
                        </div>
                    </div>
                </slot>
            </veui-link>
        </template>
    </div>
</template>

<script>
import {
    Icon,
    Link
} from 'veui';

export default {
    name: 'veui-steps',
    components: {
        'veui-icon': Icon,
        'veui-link': Link
    },
    props: {
        steps: {
            type: Array,
            default() {
                return [];
            }
        },
        current: {
            type: Number,
            default: 0
        },
        stateless: Boolean
    },
    computed: {
        stepStatus() {
            if (this.stateless) {
                return Array(this.steps.length).fill('stateless');
            }

            return this.steps.reduce((acc, step, i) => {
                let status
                    = step.status === 'success'
                        ? (
                            // i > this.current
                            //     ? 'completed'
                            //     : 'incomplete'
                            'completed'
                        )
                        : step.status === 'error'
                            ? i === this.current
                                ? 'error-current'
                                : 'error'
                            : i === this.current
                                ? 'current'
                                : i < this.current
                                    ? 'completed'
                                    : 'incomplete';

                acc.push(status);
                return acc;
            }, []);
        }
    }
};
</script>
<style lang="less" scoped>
.veui-steps {
    display: flex;
}

.veui-steps-step {
    flex: 1;
    display: flex;
    text-decoration: none;
    overflow: hidden;
    pointer-events: none;
    padding: 2px 0 0 2px;
    margin: -2px 8px 0 -2px;
}

.veui-steps-step-index-container {
    pointer-events: auto;
}

.veui-steps-step-index {
    flex-grow: 0;
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    border-width: 1px;
    border-style: solid;
    transition-property: background-color, color, box-shadow, border-color;
    transition-duration: .2s;
}

.veui-steps-step-index .veui-icon {
    width: auto;
    height: 16px;
}

.veui-steps-step-content {
    padding-left: 8px;
    pointer-events: auto;
}

.veui-steps-step-label {
    line-height: 1.4;
    color: rgba(19, 22, 26, .9);
    font-weight: 500;
    margin-right: 8px;
    display: inline-block;
    position: relative;
    max-width: 10em;
    word-wrap: break-word;
    transition-property: color;
    transition-duration: .2s;
}

.veui-steps-step-label::after {
    content: "";
    width: 9999px;
    border-top-width: 1px;
    pointer-events: none;
    margin: 0;
    position: absolute;
    left: calc(100% + 8px);
}

.veui-steps-step-desc {
    margin-top: 4px;
    margin-bottom: 0;
    color: rgba(80, 90, 107, .8);
    max-width: 12em;
    word-wrap: break-word;
}

.veui-steps-step:last-child {
    margin-right: 0;
    flex: 0 1 auto;
}

.veui-steps-step:last-child .veui-steps-step-label::after {
    display: none;
}

.veui-steps-step-connector-incomplete,
.veui-steps-step-connector-placeholder-incomplete {
    border-color: #d3d9e6;
}

.veui-steps-step-incomplete::before {
    border-color: #d3d9e6;
}

.veui-steps-step-incomplete .veui-steps-step-index {
    border-color: transparent;
    color: rgba(80, 90, 107, .8);
    background-color: #ebedf5;
}

.veui-steps-step-next-incomplete::after,
.veui-steps-step-next-incomplete .veui-steps-step-label::after,
.veui-steps-step-next-incomplete .veui-steps-step-index::after {
    border-color: #d3d9e6;
}

a.veui-steps-step-incomplete:hover .veui-steps-step-index {
    background-color: #e2e6f0;
}

a.veui-steps-step-incomplete[data-focus-visible-added] .veui-steps-step-index {
    background-color: #e2e6f0;
}

a.veui-steps-step-incomplete:active .veui-steps-step-index {
    background-color: #d3d9e6;
}

.veui-steps-step-connector-completed,
.veui-steps-step-connector-placeholder-completed {
    border-color: #0054e6;
}

.veui-steps-step-completed::before {
    border-color: #0054e6;
}

.veui-steps-step-completed .veui-steps-step-index {
    border-color: transparent;
    color: #0054e6;
    background-color: #ebf2ff;
}

.veui-steps-step-next-completed::after,
.veui-steps-step-next-completed .veui-steps-step-label::after,
.veui-steps-step-next-completed .veui-steps-step-index::after {
    border-color: #0054e6;
}

a.veui-steps-step-completed:hover .veui-steps-step-index {
    background-color: #dce9ff;
}

a.veui-steps-step-completed[data-focus-visible-added] .veui-steps-step-index {
    background-color: #dce9ff;
}

a.veui-steps-step-completed:active .veui-steps-step-index {
    background-color: #bfd7ff;
}

.veui-steps-step-connector-error,
.veui-steps-step-connector-placeholder-error {
    border-color: #d9150b;
}

.veui-steps-step-error::before {
    border-color: #d9150b;
}

.veui-steps-step-error .veui-steps-step-index {
    border-color: transparent;
    color: #d9150b;
    background-color: #ffeceb;
}

.veui-steps-step-next-error::after,
.veui-steps-step-next-error .veui-steps-step-label::after,
.veui-steps-step-next-error .veui-steps-step-index::after {
    border-color: #d9150b;
}

a.veui-steps-step-error:hover .veui-steps-step-index {
    background-color: #ffdedc;
}

a.veui-steps-step-error[data-focus-visible-added] .veui-steps-step-index {
    background-color: #ffdedc;
}

a.veui-steps-step-error:active .veui-steps-step-index {
    background-color: #ffc4c0;
}

.veui-steps-step-connector-current,
.veui-steps-step-connector-placeholder-current {
    border-color: #0054e6;
}

.veui-steps-step-current::before {
    border-color: #0054e6;
}

.veui-steps-step-current .veui-steps-step-index {
    border-color: transparent;
    color: #fff;
    background-color: #0054e6;
}

.veui-steps-step-next-current::after,
.veui-steps-step-next-current .veui-steps-step-label::after,
.veui-steps-step-next-current .veui-steps-step-index::after {
    border-color: #0054e6;
}

a.veui-steps-step-current:hover .veui-steps-step-index {
    background-color: #1a6eff;
}

a.veui-steps-step-current[data-focus-visible-added] .veui-steps-step-index {
    background-color: #1a6eff;
}

a.veui-steps-step-current:active .veui-steps-step-index {
    background-color: #0047c4;
}

.veui-steps-step-connector-error-current,
.veui-steps-step-connector-placeholder-error-current {
    border-color: #d9150b;
}

.veui-steps-step-error-current::before {
    border-color: #d9150b;
}

.veui-steps-step-error-current .veui-steps-step-index {
    border-color: transparent;
    color: #fff;
    background-color: #d9150b;
}

.veui-steps-step-next-error-current::after,
.veui-steps-step-next-error-current .veui-steps-step-label::after,
.veui-steps-step-next-error-current .veui-steps-step-index::after {
    border-color: #d9150b;
}

a.veui-steps-step-error-current:hover .veui-steps-step-index {
    background-color: #ff2b20;
}

a.veui-steps-step-error-current[data-focus-visible-added]
.veui-steps-step-index {
    background-color: #ff2b20;
}

a.veui-steps-step-error-current:active .veui-steps-step-index {
    background-color: #b80900;
}

.veui-steps-step-connector-stateless,
.veui-steps-step-connector-placeholder-stateless {
    border-color: #d3d9e6;
}

.veui-steps-step-stateless::before {
    border-color: #d3d9e6;
}

.veui-steps-step-stateless .veui-steps-step-index {
    border-color: transparent;
    color: #0054e6;
    background-color: #ebf2ff;
}

.veui-steps-step-next-stateless::after,
.veui-steps-step-next-stateless .veui-steps-step-label::after,
.veui-steps-step-next-stateless .veui-steps-step-index::after {
    border-color: #d3d9e6;
}

.veui-steps-step-incomplete .veui-steps-step-label {
    color: rgba(80, 90, 107, .8) !important;
}

.veui-steps-step-incomplete .veui-steps-step-desc {
    color: rgba(80, 90, 107, .8) !important;
}

a.veui-steps-step:hover .veui-steps-step-label {
    color: rgba(19, 22, 26, .9);
}

a.veui-steps-step:active .veui-steps-step-label {
    color: rgba(19, 22, 26, .9);
}

.veui-steps-step[data-focus-visible-added] .veui-steps-step-index {
    border-color: #0054e6;
    box-shadow: 0 0 0 2px rgba(26, 110, 255, .2);
}

.veui-steps-step[data-focus-visible-added] .veui-steps-step-label {
    color: rgba(19, 22, 26, .9);
}

.veui-steps-step-error[data-focus-visible-added] .veui-steps-step-index,
.veui-steps-step-error-current[data-focus-visible-added]
.veui-steps-step-index {
    border-color: #d9150b;
    box-shadow: 0 0 0 2px rgba(255, 43, 32, .2);
}

.veui-steps .veui-steps a:hover,
.veui-steps .veui-link:hover,
.veui-steps .veui-steps a[data-focus-visible-added],
.veui-steps .veui-link[data-focus-visible-added],
.veui-steps .veui-steps a:active,
.veui-steps .veui-link:active,
.veui-steps .veui-steps a.veui-disabled,
.veui-steps .veui-link.veui-disabled {
    text-decoration: none;
}

.veui-steps .veui-link:not(.veui-steps-step-activatable) {
    cursor: default;
}

.veui-steps[ui~="label-vertical"] .veui-steps-step {
    flex-direction: column;
    position: relative;
    margin-right: 0;
    text-align: center;
    flex: 1;
}

.veui-steps[ui~="label-vertical"] .veui-steps-step-content {
    padding-left: 0;
}

.veui-steps[ui~="label-vertical"] .veui-steps-step-label {
    margin-top: 8px;
    margin-right: 0;
}

.veui-steps[ui~="label-vertical"] .veui-steps-step-label::after {
    display: none;
}

.veui-steps[ui~="label-vertical"] .veui-steps-step-index,
.veui-steps[ui~="label-vertical"] .veui-steps-step-desc {
    margin-right: auto;
    margin-left: auto;
}

.veui-steps[ui~="label-vertical"] .veui-steps-step-connector {
    flex-grow: 0;
    content: "";
    width: 40px;
    border-top-width: 1px;
    pointer-events: none;
}

.veui-steps[ui~="label-vertical"] .veui-steps-step::after {
    content: "";
    width: 9999px;
    border-top-width: 1px;
    pointer-events: none;
    margin: 0;
    position: absolute;
}

.veui-steps[ui~="label-vertical"] .veui-steps-step::before {
    content: "";
    width: 9999px;
    border-top-width: 1px;
    pointer-events: none;
    margin: 0;
    position: absolute;
}

.veui-steps[ui~="label-vertical"] .veui-steps-step:first-child::before,
.veui-steps[ui~="label-vertical"] .veui-steps-step:last-child::after {
    display: none;
}

.veui-steps[ui~="vertical"] {
    display: inline-flex;
    flex-direction: column;
}

.veui-steps[ui~="vertical"] .veui-steps-step {
    margin-right: 0;
    margin-top: 6px;
    display: flex;
    overflow: initial;
}

.veui-steps[ui~="vertical"] .veui-steps-step-index-container {
    display: flex;
    flex-direction: column;
}

.veui-steps[ui~="vertical"]
.veui-steps-step-index-container
.veui-steps-step-connector-placeholder {
    flex-grow: 1;
    border-left-width: 1px;
    min-height: 32px;
    margin-top: 8px;
    pointer-events: none;
}

.veui-steps[ui~="vertical"] .veui-steps-step-index {
    position: relative;
    margin-top: 0;
    flex-grow: 0;
}

.veui-steps[ui~="vertical"] .veui-steps-step-label,
.veui-steps[ui~="vertical"] .veui-steps-step-desc {
    max-width: inherit;
}

.veui-steps[ui~="vertical"] .veui-steps-step-connector {
    height: 8px;
    border-left-width: 1px;
    border-left-style: solid;
    pointer-events: none;
}

.veui-steps[ui~="vertical"] .veui-steps-step-label::after,
.veui-steps[ui~="vertical"]
.veui-steps-step:last-child
.veui-steps-step-index::after {
    display: none;
}

.veui-steps[ui~="vertical"] .veui-steps-step:first-child {
    margin-top: -2px;
}

.veui-steps[ui~="m"] .veui-steps-step-index {
    width: 28px;
    height: 28px;
    line-height: 26px;
    font-size: 16px;
}

.veui-steps[ui~="m"] .veui-steps-step-label {
    margin-top: 4.2px;
    font-size: 14px;
}

.veui-steps[ui~="m"] .veui-steps-step-label::after {
    top: 9.3px;
}

.veui-steps[ui~="m"] .veui-steps-step-desc {
    font-size: 12px;
}

.veui-steps[ui~="m"][ui~="label-vertical"] .veui-steps-step-connector {
    margin-top: 13.5px;
}

.veui-steps[ui~="m"][ui~="label-vertical"] .veui-steps-step::after {
    top: 15.5px;
    left: calc(50% + 22px);
}

.veui-steps[ui~="m"][ui~="label-vertical"] .veui-steps-step::before {
    top: 15.5px;
    right: calc(50% + 22px);
}

.veui-steps[ui~="m"][ui~="vertical"] .veui-steps-step-index::after {
    top: 36px;
    left: 14px;
}

.veui-steps[ui~="m"][ui~="vertical"] .veui-steps-step-connector-placeholder {
    margin-left: 13.5px;
}

.veui-steps[ui~="m"][ui~="vertical"] .veui-steps-step-connector {
    margin-left: 13.5px;
}

.veui-steps[ui~="s"] .veui-steps-step-index {
    width: 28px;
    height: 28px;
    line-height: 26px;
    font-size: 16px;
}

.veui-steps[ui~="s"] .veui-steps-step-label {
    margin-top: 5.6px;
    font-size: 12px;
}

.veui-steps[ui~="s"] .veui-steps-step-label::after {
    top: 7.9px;
}

.veui-steps[ui~="s"] .veui-steps-step-desc {
    font-size: 12px;
}

.veui-steps[ui~="s"][ui~="label-vertical"] .veui-steps-step-connector {
    margin-top: 13.5px;
}

.veui-steps[ui~="s"][ui~="label-vertical"] .veui-steps-step::after {
    top: 15.5px;
    left: calc(50% + 22px);
}

.veui-steps[ui~="s"][ui~="label-vertical"] .veui-steps-step::before {
    top: 15.5px;
    right: calc(50% + 22px);
}

.veui-steps[ui~="s"][ui~="vertical"] .veui-steps-step-index::after {
    top: 36px;
    left: 14px;
}

.veui-steps[ui~="s"][ui~="vertical"] .veui-steps-step-connector-placeholder {
    margin-left: 13.5px;
}

.veui-steps[ui~="s"][ui~="vertical"] .veui-steps-step-connector {
    margin-left: 13.5px;
}

.veui-steps[ui~="dot"] .veui-steps-step-index {
    border-style: solid;
    color: transparent;
}

.veui-steps[ui~="dot"] .veui-steps-step-index .veui-icon {
    display: none;
}

.veui-steps[ui~="dot"] .veui-steps-step-incomplete .veui-steps-step-index {
    outline: 1px solid transparent;
    outline-offset: -1px;
    border-color: #a8b0bf;
    background-color: transparent;
}

.veui-steps[ui~="dot"]
a.veui-steps-step-incomplete:hover
.veui-steps-step-index {
    border-color: #848b99;
    background-color: transparent;
}

.veui-steps[ui~="dot"]
a.veui-steps-step-incomplete[data-focus-visible-added]
.veui-steps-step-index {
    border-color: #848b99;
    background-color: transparent;
}

.veui-steps[ui~="dot"]
a.veui-steps-step-incomplete:active
.veui-steps-step-index {
    border-color: #545b66;
    background-color: transparent;
}

.veui-steps[ui~="dot"] .veui-steps-step-completed .veui-steps-step-index {
    outline: 1px solid transparent;
    outline-offset: -1px;
    border-color: #0054e6;
    background-color: transparent;
}

.veui-steps[ui~="dot"]
a.veui-steps-step-completed:hover
.veui-steps-step-index {
    border-color: #1a6eff;
    background-color: transparent;
}

.veui-steps[ui~="dot"]
a.veui-steps-step-completed[data-focus-visible-added]
.veui-steps-step-index {
    border-color: #1a6eff;
    background-color: transparent;
}

.veui-steps[ui~="dot"]
a.veui-steps-step-completed:active
.veui-steps-step-index {
    border-color: #0047c4;
    background-color: transparent;
}

.veui-steps[ui~="dot"] .veui-steps-step-error .veui-steps-step-index {
    outline: 1px solid transparent;
    outline-offset: -1px;
    border-color: #d9150b;
    background-color: transparent;
}

.veui-steps[ui~="dot"] a.veui-steps-step-error:hover .veui-steps-step-index {
    border-color: #ff2b20;
    background-color: transparent;
}

.veui-steps[ui~="dot"]
a.veui-steps-step-error[data-focus-visible-added]
.veui-steps-step-index {
    border-color: #ff2b20;
    background-color: transparent;
}

.veui-steps[ui~="dot"] a.veui-steps-step-error:active .veui-steps-step-index {
    border-color: #b80900;
    background-color: transparent;
}

.veui-steps[ui~="dot"] .veui-steps-step-current .veui-steps-step-index {
    outline: 1px solid transparent;
    outline-offset: -1px;
    border-color: #0054e6;
    background-color: transparent;
}

.veui-steps[ui~="dot"] a.veui-steps-step-current:hover .veui-steps-step-index {
    border-color: #1a6eff;
    background-color: transparent;
}

.veui-steps[ui~="dot"]
a.veui-steps-step-current[data-focus-visible-added]
.veui-steps-step-index {
    border-color: #1a6eff;
    background-color: transparent;
}

.veui-steps[ui~="dot"] a.veui-steps-step-current:active .veui-steps-step-index {
    border-color: #0047c4;
    background-color: transparent;
}

.veui-steps[ui~="dot"] .veui-steps-step-error-current .veui-steps-step-index {
    outline: 1px solid transparent;
    outline-offset: -1px;
    border-color: #d9150b;
    background-color: transparent;
}

.veui-steps[ui~="dot"]
a.veui-steps-step-error-current:hover
.veui-steps-step-index {
    border-color: #ff2b20;
    background-color: transparent;
}

.veui-steps[ui~="dot"]
a.veui-steps-step-error-current[data-focus-visible-added]
.veui-steps-step-index {
    border-color: #ff2b20;
    background-color: transparent;
}

.veui-steps[ui~="dot"]
a.veui-steps-step-error-current:active
.veui-steps-step-index {
    border-color: #b80900;
    background-color: transparent;
}

.veui-steps[ui~="dot"] .veui-steps-step-stateless .veui-steps-step-index {
    outline: 1px solid transparent;
    outline-offset: -1px;
    border-color: #0054e6;
    background-color: transparent;
}

.veui-steps[ui~="dot"]
.veui-steps-step[data-focus-visible-added]
.veui-steps-step-index {
    outline-color: #0054e6;
}

.veui-steps[ui~="dot"]
.veui-steps-step-error[data-focus-visible-added]
.veui-steps-step-index,
.veui-steps[ui~="dot"]
.veui-steps-step-error-current[data-focus-visible-added]
.veui-steps-step-index {
    outline-color: #d9150b;
}

.veui-steps[ui~="dot"][ui~="m"] .veui-steps-step-index {
    width: 10px;
    height: 10px;
    border-width: 2px;
    line-height: 8px;
}

.veui-steps[ui~="dot"][ui~="m"] .veui-steps-step-label {
    margin-top: -4.8px;
}

.veui-steps[ui~="dot"][ui~="m"] .veui-steps-step-label::after {
    top: 9.3px;
}

.veui-steps[ui~="dot"][ui~="m"] .veui-steps-step-current .veui-steps-step-index,
.veui-steps[ui~="dot"][ui~="m"]
.veui-steps-step-error-current
.veui-steps-step-index {
    border-width: 5px;
}

.veui-steps[ui~="dot"][ui~="m"][ui~="label-vertical"]
.veui-steps-step-connector {
    margin-top: 4.5px;
}

.veui-steps[ui~="dot"][ui~="m"][ui~="label-vertical"] .veui-steps-step::after {
    top: 6.5px;
    left: calc(50% + 13px);
}

.veui-steps[ui~="dot"][ui~="m"][ui~="label-vertical"] .veui-steps-step::before {
    top: 6.5px;
    right: calc(50% + 13px);
}

.veui-steps[ui~="dot"][ui~="m"][ui~="vertical"]
.veui-steps-step ~ .veui-steps-step {
    margin-top: 1.2px;
}

.veui-steps[ui~="dot"][ui~="m"][ui~="vertical"]
.veui-steps-step-index-container {
    margin-top: 4.8px;
}

.veui-steps[ui~="dot"][ui~="m"][ui~="vertical"] .veui-steps-step-index::after {
    top: 18px;
    left: 5px;
}

.veui-steps[ui~="dot"][ui~="m"][ui~="vertical"]
.veui-steps-step-connector-placeholder {
    margin-left: 4.5px;
}

.veui-steps[ui~="dot"][ui~="m"][ui~="vertical"] .veui-steps-step-connector {
    margin-left: 4.5px;
}

.veui-steps[ui~="dot"][ui~="s"] .veui-steps-step-index {
    width: 10px;
    height: 10px;
    border-width: 2px;
    line-height: 8px;
}

.veui-steps[ui~="dot"][ui~="s"] .veui-steps-step-label {
    margin-top: -3.4px;
}

.veui-steps[ui~="dot"][ui~="s"] .veui-steps-step-label::after {
    top: 7.9px;
}

.veui-steps[ui~="dot"][ui~="s"] .veui-steps-step-current .veui-steps-step-index,
.veui-steps[ui~="dot"][ui~="s"]
.veui-steps-step-error-current
.veui-steps-step-index {
    border-width: 5px;
}

.veui-steps[ui~="dot"][ui~="s"][ui~="label-vertical"]
.veui-steps-step-connector {
    margin-top: 4.5px;
}

.veui-steps[ui~="dot"][ui~="s"][ui~="label-vertical"] .veui-steps-step::after {
    top: 6.5px;
    left: calc(50% + 13px);
}

.veui-steps[ui~="dot"][ui~="s"][ui~="label-vertical"] .veui-steps-step::before {
    top: 6.5px;
    right: calc(50% + 13px);
}

.veui-steps[ui~="dot"][ui~="s"][ui~="vertical"]
.veui-steps-step ~ .veui-steps-step {
    margin-top: 2.6px;
}

.veui-steps[ui~="dot"][ui~="s"][ui~="vertical"]
.veui-steps-step-index-container {
    margin-top: 3.4px;
}

.veui-steps[ui~="dot"][ui~="s"][ui~="vertical"] .veui-steps-step-index::after {
    top: 18px;
    left: 5px;
}

.veui-steps[ui~="dot"][ui~="s"][ui~="vertical"]
.veui-steps-step-connector-placeholder {
    margin-left: 4.5px;
}

.veui-steps[ui~="dot"][ui~="s"][ui~="vertical"] .veui-steps-step-connector {
    margin-left: 4.5px;
}

.veui-steps-step-connector,
.veui-steps-step-label::after,
.veui-steps[ui~="label-vertical"] .veui-steps-step::before,
.veui-steps[ui~="label-vertical"] .veui-steps-step::after {
    border-top-style: solid;
}

.veui-steps-step-connector-stateless,
.veui-steps-step-stateless .veui-steps-step-label::after,
.veui-steps[ui~="label-vertical"] .veui-steps-step-stateless::before,
.veui-steps[ui~="label-vertical"] .veui-steps-step-stateless::after {
    border-top-style: dashed;
}

.veui-steps[ui~="vertical"] .veui-steps-step-connector,
.veui-steps[ui~="vertical"] .veui-steps-step-connector-placeholder {
    border-top-style: none;
    border-left-style: solid;
}

.veui-steps[ui~="vertical"] .veui-steps-step-connector-stateless,
.veui-steps[ui~="vertical"] .veui-steps-step-connector-placeholder-stateless {
    border-top-style: none;
    border-left-style: dashed;
}
</style>
